import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQueryClient } from '@tanstack/react-query'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'

import { Modal } from '@ui/components/Modal'
import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'

import { PageQuery, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useDebounceEffect } from '@utils/debounce'

import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollIntoViewOnPagingEntriesChange,
} from '@hooks/useScrollToElementIds'

import { Box } from '@shared/components/Box'

import { SubIBAccountsTable } from './SubIBAccountsTable'
import { AddEditSubIBModal } from './modals/AddEditSubIB.modal'

import styles from './SubIBAccountsPage.module.scss'

export const SubIBAccountsPage: FC = () => {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string | null>(null)
  useDebounceEffect(() => setDebouncedSearchValue(searchValue), [searchValue], 500)

  const { account } = useAccountReadContext()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const introducingBrokerId = account?.clientIntroducingBroker?.id || ''

  const tableQuery = usePaginatedQuery({
    queryKey: queryKeys.ib.subAccounts(introducingBrokerId),
    queryFn: (pageQuery) =>
      apiClient.getIBSubAccounts(introducingBrokerId, {
        ...pageQuery,
        caller: 'ca',
      }),
  })

  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)

  useScrollIntoViewOnPagingEntriesChange(
    ScrollToIds.MultiTierSubIBAccounts,
    isPaginationEntrySelected,
    tableQuery.isLoading,
    setIsPaginationEntrySelected
  )
  useScrollAfterLoad(
    ScrollToIds.MultiTierSubIBAccounts,
    tableQuery.isLoading,
    tableQuery.meta?.pageSize
  )

  const renderListItem = (title: string, text: string) => (
    <Modal.Body.Text>
      {title && <Modal.Body.Text medium>{title}: </Modal.Body.Text>}
      {text}
    </Modal.Body.Text>
  )

  const handleSubIB = async (subAccountId: number, commissionPlanGroupId: string) => {
    addEditSubIBModal.close()
    const introducingBrokerId = account?.clientIntroducingBroker?.id || ''
    await apiClient.updateIBSubAccount(introducingBrokerId, subAccountId, commissionPlanGroupId)
    queryClient.invalidateQueries({ queryKey: queryKeys.ib.subAccounts(introducingBrokerId) })
    tableQuery.forceRefresh()
  }

  const addEditSubIBModal = useModal(AddEditSubIBModal, {
    handleSubIB,
  })

  const handlePageChange = (pageIndex: number, pageSize?: number) => {
    if (pageSize && pageSize !== tableQuery.meta?.pageSize) {
      setIsPaginationEntrySelected(true)
    }
    tableQuery.setPageQuery!({
      ...tableQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  useEffect(() => {
    if (debouncedSearchValue !== null) {
      tableQuery.setPageQuery!((oldPageQuery: PageQuery) => ({
        ...oldPageQuery,
        pageIndex: 1,
        ca_search_TextSearch: debouncedSearchValue,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue])

  const table = (
    <>
      <SubIBAccountsTable
        tableQuery={tableQuery}
        onEdit={(group) => addEditSubIBModal.open({ data: group })}
        searchValue={debouncedSearchValue}
      />
      {tableQuery.meta && (
        <Pagination
          scrollToHeaderId={ScrollToIds.MultiTierSubIBAccounts}
          maxPageSize={100}
          pageData={tableQuery.meta}
          isLoading={tableQuery.isLoading}
          onPageChanged={handlePageChange}
        />
      )}
    </>
  )

  return (
    <div className={styles.subIBAccountsPage}>
      <PageSection
        title={t('IB.MultiTier.Sub-IB Accounts')}
        id={ScrollToIds.MultiTierSubIBAccounts}
        searchField={{
          onChange: setSearchValue,
          value: searchValue,
          disabled: false,
          canClear: true,
          renderResult: () => table,
        }}
        infoModalData={{
          title: t('IB.MultiTier.Sub-IB Accounts'),
          body: (
            <Box flex direction='column' gap='x16'>
              {renderListItem('', t('IB.MultiTier.IB accounts in your network'))}
              {renderListItem(t('IB.Name'), t('IB.MultiTier.Name of the IB in your network'))}
              {renderListItem(t('IB.Volume'), t('IB.MultiTier.Total volume the IB'))}
              <Modal.Body.Text>
                {t('IB.MultiTier.You can change the IB Commission Plan for a sub-IB')}
              </Modal.Body.Text>
            </Box>
          ),
        }}
      />
      {table}
    </div>
  )
}
